"use client";
import { useButton } from "../button/button.js";
import { useDisclosureProviderContext } from "./disclosure-context.js";
import { createElement, createHook, forwardRef, useBooleanEvent, useEvent, useMergeRefs, useMetadataProps } from "@ariakit/react-utils";
import { invariant } from "@ariakit/utils";
import { useEffect, useRef, useState } from "react";
import { useStoreState } from "@ariakit/react-store";
//#region src/disclosure/disclosure.tsx
const TagName = "button";
const symbol = Symbol("disclosure");
/**
* Returns props to create a `Disclosure` component.
* @see https://ariakit.com/components/disclosure
* @example
* ```jsx
* const store = useDisclosureStore();
* const props = useDisclosure({ store });
* <Role {...props}>Disclosure</Role>
* <DisclosureContent store={store}>Content</DisclosureContent>
* ```
*/
const useDisclosure = createHook(function useDisclosure({ store, toggleOnClick = true, ...props }) {
	const context = useDisclosureProviderContext();
	store = store || context;
	invariant(store, process.env.NODE_ENV !== "production" && "Disclosure must receive a `store` prop or be wrapped in a DisclosureProvider component.");
	const ref = useRef(null);
	const [expanded, setExpanded] = useState(false);
	const disclosureElement = useStoreState(store, "disclosureElement");
	const open = useStoreState(store, "open");
	useEffect(() => {
		let isCurrentDisclosure = disclosureElement === ref.current;
		if (!disclosureElement?.isConnected) {
			store?.setDisclosureElement(ref.current);
			isCurrentDisclosure = true;
		}
		setExpanded(open && isCurrentDisclosure);
	}, [
		disclosureElement,
		store,
		open
	]);
	const onClickProp = props.onClick;
	const toggleOnClickProp = useBooleanEvent(toggleOnClick);
	const [isDuplicate, metadataProps] = useMetadataProps(props, symbol, true);
	const onClick = useEvent((event) => {
		onClickProp?.(event);
		if (event.defaultPrevented) return;
		if (isDuplicate) return;
		if (!toggleOnClickProp(event)) return;
		store?.setDisclosureElement(event.currentTarget);
		store?.toggle();
	});
	props = {
		"aria-expanded": expanded,
		"aria-controls": useStoreState(store, "contentElement")?.id,
		...metadataProps,
		...props,
		ref: useMergeRefs(ref, props.ref),
		onClick
	};
	props = useButton(props);
	return props;
});
/**
* Renders an element that controls the visibility of a
* [`DisclosureContent`](https://ariakit.com/reference/disclosure-content)
* element.
* @see https://ariakit.com/components/disclosure
* @example
* ```jsx {2}
* <DisclosureProvider>
*   <Disclosure>Disclosure</Disclosure>
*   <DisclosureContent>Content</DisclosureContent>
* </DisclosureProvider>
* ```
*/
const Disclosure = forwardRef(function Disclosure(props) {
	return createElement(TagName, useDisclosure(props));
});
//#endregion
export { Disclosure, useDisclosure };

//# sourceMappingURL=disclosure.js.map